/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useSelector } from 'react-redux';
import {
    Box,
    Button,
    DisplayHtmlContent,
    Heading,
    Icon,
    Stack,
    FlexRow,
    FlexCol,
    Alert,
} from 'sarsaparilla';

import { permitSelector } from '../slices/permitContent';

import { LayoutControlContext } from './DetailedAvailabilityPageLayout';
import ExpandMainViewButton from './ExpandMainViewButton';
import DetailedAvailabilityFilter from './DetailedAvailabilityFilter';
import DetailedAvailabilityGridAndActions from './DetailedAvailabilityGridAndActions';
import DetailedAvailabilityGridAndActionsNew from './DetailedAvailabilityGridAndActionsNew';
import DetailedAvailabilityInformationRequired from './DetailedAvailabilityInformationRequired';
import ErrorNotification from '../common/NewErrorNotification';

function DetailedAvailabilityPageMain({
    errorBody,
    errorDismiss,
    errorIsShow,
    warningBody,
    warningDismiss,
    warningIsShow,
    children,
}) {
    const permit = useSelector(permitSelector.getData);
    const {
        isFullView,
        isInformationRequired,
        isMobileApp,
        isShowAvailabilityFilters,
        setIsFullView,
        setIsShowAvailabilityFilters,
    } = useContext(LayoutControlContext);

    const handleToggleFullView = useCallback(() => {
        setIsFullView((prevIsFullView) => !prevIsFullView);
    }, [setIsFullView]);

    const childrenArray = React.Children.toArray(children);

    const detailedAvailabilityGridAndActions = childrenArray.find(({ type }) =>
        [
            DetailedAvailabilityGridAndActions,
            DetailedAvailabilityGridAndActionsNew,
        ].includes(type)
    );
    const detailedAvailabilityInformationRequired = childrenArray.find(
        ({ type }) => type === DetailedAvailabilityInformationRequired
    );
    const detailedAvailabilityExtraComponents = childrenArray.filter(
        ({ props }) => props.isAllowOnDetailedAvailability
    );

    let detailedAvailabilityFilterWithProps = childrenArray.find(
        ({ type }) => type === DetailedAvailabilityFilter
    );
    if (
        detailedAvailabilityFilterWithProps &&
        React.isValidElement(detailedAvailabilityFilterWithProps)
    ) {
        detailedAvailabilityFilterWithProps = React.cloneElement(
            detailedAvailabilityFilterWithProps,
            undefined,
            [
                ...React.Children.toArray(
                    detailedAvailabilityFilterWithProps.props.children
                ),
                detailedAvailabilityInformationRequired,
            ]
        );
    }

    const renderNotifications = () => {
        return (
            (errorIsShow || warningIsShow) && (
                <div
                    className={cx('per-availability-notification', {
                        'mobile-app': isMobileApp,
                    })}
                    aria-live="polite"
                >
                    <ErrorNotification
                        className="mb-2"
                        body={errorBody}
                        dismiss={errorDismiss}
                        isShow={errorIsShow}
                    />
                    {warningIsShow && (
                        <Alert
                            type="warning"
                            className="mb-2"
                            onCloseButtonClick={warningDismiss}
                            showCloseButton
                            hasAriaLive
                        >
                            {warningBody}
                        </Alert>
                    )}
                </div>
            )
        );
    };

    return (
        <div
            id="per-availability-main"
            className={cx('per-availability-main', {
                expand: isFullView,
                'mobile-app': !isShowAvailabilityFilters && isMobileApp,
            })}
        >
            <Stack role="main" space="md">
                {isMobileApp && !isShowAvailabilityFilters && renderNotifications()}

                {!!permit?.description?.availability_grid_alt &&
                    !isShowAvailabilityFilters && (
                        <Box className="rec-sr-only">
                            <DisplayHtmlContent
                                html={permit?.description?.availability_grid_alt}
                            />
                        </Box>
                    )}

                {detailedAvailabilityFilterWithProps}

                {!isMobileApp && renderNotifications()}

                <FlexRow justifyContent="end" className="mt-2">
                    {(!isShowAvailabilityFilters || !isMobileApp) && (
                        <FlexCol>
                            <Heading headingLevel={1} appearance="h4">
                                {isMobileApp ? (
                                    <Button
                                        appearance="pill"
                                        size="md"
                                        onClick={() => setIsShowAvailabilityFilters(true)}
                                        iconBeforeElement={<Icon iconName="arrow-back" />}
                                    >
                                        Change filters
                                    </Button>
                                ) : (
                                    'Detailed Availability'
                                )}
                            </Heading>
                        </FlexCol>
                    )}
                    {(!isMobileApp || isShowAvailabilityFilters !== isMobileApp) && (
                        <FlexCol xs="variable" className="mr-2 pt-half">
                            <ExpandMainViewButton
                                isExpanded={isFullView}
                                onClick={handleToggleFullView}
                            />
                        </FlexCol>
                    )}
                </FlexRow>

                {detailedAvailabilityExtraComponents}

                {!!permit?.description?.availability_grid &&
                    !isShowAvailabilityFilters && (
                        <DisplayHtmlContent
                            html={permit?.description?.availability_grid}
                        />
                    )}

                {isInformationRequired &&
                    !isMobileApp &&
                    detailedAvailabilityInformationRequired}

                {detailedAvailabilityGridAndActions}
            </Stack>
        </div>
    );
}

DetailedAvailabilityPageMain.propTypes = {
    errorIsShow: PropTypes.bool,
    errorDismiss: PropTypes.func,
    errorBody: PropTypes.string,
    warningIsShow: PropTypes.bool,
    warningDismiss: PropTypes.func,
    warningBody: PropTypes.string,
    children: PropTypes.node,
};

export default DetailedAvailabilityPageMain;
